import { editorOptions, editorOptionsSmall } from "./JsEditorConsts";
import Editor from "@monaco-editor/react";
import { FormOutlined } from "@ant-design/icons";
import "./JsEditor.scss";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Row } from "antd";
import type { editor } from "monaco-editor";
import type * as Monaco from "monaco-editor/esm/vs/editor/editor.api";
import { InfoTooltip } from "../../BaseComps/InfoTooltip";
import { observer } from "mobx-react-lite";
import { useMst } from "../../stores/main";
import { buildJsEditorRuntimeCompletionContext } from "./jsEditorRuntimeContext";
import type { JsEditorRuntimeContext } from "./jsEditorRuntimeContext";
import {
  clearJsEditorModelRuntimeContext,
  ensureJsEditorRuntimeContext,
  setJsEditorModelRuntimeContext,
} from "./monacoRuntimeContext";

type MonacoApi = typeof Monaco;

type JsEditorProps = {
  onChange: (e: string) => void;
  defaultValue?: string;
  height?: string;
  style?: CSSProperties;
  tooltipText?: string;
  runtimeContext?: JsEditorRuntimeContext;
};

export const JsEditor = observer(function JsEditor({
  onChange,
  defaultValue,
  height,
  style,
  tooltipText,
  runtimeContext,
}: JsEditorProps) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editorValue, setEditorValue] = useState(defaultValue);
  const modalEditorRef = useRef<editor.IStandaloneCodeEditor | undefined>();
  const mountedEditorsRef = useRef<Set<editor.IStandaloneCodeEditor>>(
    new Set()
  );
  const {
    dynamicDataStore: { perVideoCurrentSchema, liveControlCurrentSchema },
    platformStore: { inputNames, isControlDeprecated },
  } = useMst();
  const inputNamesKey = JSON.stringify(inputNames);
  const inputNamesForCompletion = useMemo(
    () => JSON.parse(inputNamesKey) as string[],
    [inputNamesKey]
  );
  const runtimeCompletionContext = useMemo(
    () =>
      buildJsEditorRuntimeCompletionContext({
        runtimeContext,
        perVideoSchema: perVideoCurrentSchema,
        liveControlSchema: liveControlCurrentSchema,
        inputNames: inputNamesForCompletion,
        isControlDeprecated,
      }),
    [
      runtimeContext,
      perVideoCurrentSchema,
      liveControlCurrentSchema,
      inputNamesForCompletion,
      isControlDeprecated,
    ]
  );

  useEffect(() => {
    setEditorValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    mountedEditorsRef.current.forEach((mountedEditor) => {
      setJsEditorModelRuntimeContext(
        mountedEditor.getModel(),
        runtimeCompletionContext
      );
    });
  }, [runtimeCompletionContext]);

  useEffect(() => {
    const mountedEditors = mountedEditorsRef.current;
    return () => {
      mountedEditors.forEach((mountedEditor) => {
        clearJsEditorModelRuntimeContext(mountedEditor.getModel());
      });
      mountedEditors.clear();
    };
  }, []);

  const handleClose = () => {
    setIsModalVisible(false);
  };

  const handleOpen = () => {
    setIsModalVisible(true);
  };

  const handleOnChange = (value?: string) => {
    const nextValue = value ?? "";
    setEditorValue(nextValue);
    onChange(nextValue);
  };

  function handleEditorWillMount(monaco: MonacoApi) {
    ensureJsEditorRuntimeContext(monaco);
  }

  function handleEditorDidMount(
    mountedEditor: editor.IStandaloneCodeEditor,
    monaco: MonacoApi
  ) {
    ensureJsEditorRuntimeContext(monaco);
    mountedEditorsRef.current.add(mountedEditor);
    setJsEditorModelRuntimeContext(
      mountedEditor.getModel(),
      runtimeCompletionContext
    );
  }

  function handleModalEditorDidMount(
    mountedEditor: editor.IStandaloneCodeEditor,
    monaco: MonacoApi
  ) {
    modalEditorRef.current = mountedEditor;
    handleEditorDidMount(mountedEditor, monaco);
  }

  return (
    <div style={{ width: "100%", ...style }} className="JsEditor">
      <Row>
        <FormOutlined className="OpenWindow" onClick={handleOpen} />
        {tooltipText && (
          <InfoTooltip style={{ marginLeft: ".5rem" }} info={tooltipText} />
        )}
      </Row>

      <Editor
        language="javascript"
        defaultValue={defaultValue}
        height={height || "200px"}
        onChange={handleOnChange}
        options={editorOptionsSmall}
        theme="vs-dark"
        className="Editor"
        value={editorValue}
        beforeMount={handleEditorWillMount}
        onMount={handleEditorDidMount}
      />
      <Modal
        width={700}
        styles={{ body: { height: "25rem", maxHeight: "30rem" } }}
        className="JsEditorModal"
        open={isModalVisible}
        onOk={handleClose}
        onCancel={handleClose}
        footer={[
          <Button
            key={"indent"}
            onClick={() => {
              if (modalEditorRef.current) {
                modalEditorRef.current
                  .getAction("editor.action.formatDocument")
                  .run();
              }
            }}
          >
            Auto Indent
          </Button>,
          <Button key="ok" onClick={handleClose}>
            ok
          </Button>,
        ]}
      >
        <Editor
          language="javascript"
          defaultValue={defaultValue}
          onChange={handleOnChange}
          options={editorOptions}
          theme="vs-dark"
          className="ModalEditor"
          value={editorValue}
          beforeMount={handleEditorWillMount}
          onMount={handleModalEditorDidMount}
        />
      </Modal>
    </div>
  );
});
